import * as ids from 'short-id';
import { ITextOverlayV2 } from 'blocks/TextOverlayModal/v2';
import {
  DynamicTitleType,
  TextIntegrationId,
} from 'components/VideoTemplateEditor/types';
import { DeepImmutableMap, DeepPartial, Dimensions } from 'types';
import { domTreeToHtmlString, htmlStringToTreeWalker } from 'utils/dom';
import { getTextFitFontSize } from 'utils/embed/text-overlay';
import { Measurement, Pixels } from 'utils/measurement';
import { measurementToViewport } from 'utils/placement';
import { createNewLayer, deleteLayer } from '../../shared/layers';
import { updateState } from '../../shared/mutations';
import { TextOverlay, VideoTemplateStateContent } from '../../types';
import { AddTextOverlayOptions } from './types';
import { overlayToState } from './utils';

/**
 * Builds an editor patch carrying the auto-fitted font size returned by
 * `getTextFitFontSize`, converted into the units `editor.textStyle.fontSize`
 * and `editor.scaler.fontSizeVw` are stored in.
 *
 * `getTextFitFontSize` only writes the shrunk size into the `textHtml` inline
 * style — it never updates the overlay state. Without this patch the editor
 * keeps the stale pre-fit `fontSize` and only the cascaded `textHtml` value
 * renders correctly, leaving the toolbar input and the `fontSizeScaler`
 * pipeline out of sync.
 *
 * `editor.scaler.fontSizeVw` is a vw value, so `editor.textStyle.fontSize` is
 * just `fontSizeVw / (fontWidthPercPerPx * 100)`. Both fields are written
 * together so the `fontWidthPercPerPx` scaler ratio stays invariant.
 *
 * Returns `undefined` for legacy overlays without an editor scaler — leaving
 * the editor state untouched.
 */
function buildFittedEditorPatch(
  overlay: TextOverlay,
  fittedSize: Measurement,
): Pick<DeepPartial<TextOverlay>, 'editor'>['editor'] {
  const fontWidthPercPerPx = overlay.editor?.scaler?.fontWidthPercPerPx;

  if (!fontWidthPercPerPx) {
    return undefined;
  }

  // Only width matters for the px<->vw conversion; height is required by the
  // Measurement API but unused.
  const fittedVw = fittedSize.toUnit('vw', {
    width: new Pixels(1000),
    height: new Pixels(1000),
  }).value;

  const fontSize = Math.round(fittedVw / (fontWidthPercPerPx * 100));

  return {
    textStyle: { fontSize },
    scaler: { fontSizeVw: fontSize * fontWidthPercPerPx * 100 },
  };
}

function replaceOverlayText(
  state: VideoTemplateStateContent,
  overlayId: string,
  replacementText: string,
): Pick<DeepPartial<TextOverlay>, 'textHtml' | 'editor'> {
  const { aspectRatio, textOverlays } = state;
  const overlay = textOverlays.data[overlayId];
  const { size, textHtml } = overlay;

  const walker = htmlStringToTreeWalker(textHtml, NodeFilter.SHOW_TEXT);
  const root = walker.currentNode;

  let textNode = walker.nextNode();

  // this condition is still kept, if no text node at all is available it will throw an error
  if (!textNode) {
    throw new Error('Could not find text to replace');
  }

  // the walker is iterated until finding the node that matches the text value
  // this will replace text with the target value only if it matches the current text:
  // - if text is %%Episode%% or %%Podcast it will only fit episode on the node that has
  // the %%Episode%% as content.
  // - a corner case would be that the target text was a custom one and the content is the
  // same. In that case the replacement should not be harmful as it would replace one text
  // with the same text. This also covers the case of some templates that have "Episode Title"
  // or "Podcast Title" instead of the placeholders mentioned above.
  while (textNode && textNode.textContent !== overlay.text) {
    textNode = walker.nextNode();
  }

  // Replacement is only perfomed if the text node is actually found, otherwise no
  // replacement will be done.
  let fittedSize: Measurement | undefined;
  if (textNode) {
    // replace text
    textNode.textContent = replacementText;

    // this returns the font size, but also leaves the last "good" font size set
    // on the element
    fittedSize = getTextFitFontSize(
      textNode,
      size as any,
      overlay.style,
      aspectRatio,
    );
  }

  const newTextHtml = domTreeToHtmlString(root);
  const editor = fittedSize
    ? buildFittedEditorPatch(overlay, fittedSize)
    : undefined;

  return editor ? { textHtml: newTextHtml, editor } : { textHtml: newTextHtml };
}

export function updateTextOverlayText(
  state: VideoTemplateStateContent,
  id: string,
  value: string,
  opts?: AddTextOverlayOptions,
): VideoTemplateStateContent {
  const { textOverlays } = state;
  const textOverlay = textOverlays.data[id];

  if (!textOverlay) {
    return state;
  }

  const overlayUpdate = replaceOverlayText(state, id, value);
  return updateTextOverlay(state, id, {
    ...overlayUpdate,
    text: value,
    ...opts,
  });
}

export function addTextOverlay(
  state: VideoTemplateStateContent,
  textOverlay: DeepImmutableMap<ITextOverlayV2>,
  opts?: AddTextOverlayOptions,
): VideoTemplateStateContent {
  if (!textOverlay) return state;

  const id = ids.generate();
  const integrationData = opts?.integrationData;

  const [layerId, layers] = createNewLayer(state, 'text');

  const newOverlay: TextOverlay = {
    id,
    layerId,
    integrationData,
    ...overlayToState(textOverlay),
  };

  return updateState(state, {
    layers,
    textOverlays: {
      data: {
        [id]: newOverlay,
      },
      order: [...state.textOverlays?.order, id],
    },
  });
}

export function updateTextOverlay(
  state: VideoTemplateStateContent,
  id: string,
  textOverlay: DeepPartial<TextOverlay>,
): VideoTemplateStateContent {
  return updateState(state, {
    textOverlays: {
      data: {
        [id]: textOverlay,
      },
    },
  });
}

export function updateDynamicTextElements(
  state: VideoTemplateStateContent,
  id: string,
  textOverlay: TextOverlay,
  value: string,
  type: DynamicTitleType,
): VideoTemplateStateContent {
  const overlayUpdate = replaceOverlayText(state, id, value);

  return updateState(state, {
    textOverlays: {
      data: {
        [id]: {
          ...textOverlay,
          ...overlayUpdate,
          text: value,
          integrationData: {
            id: TextIntegrationId.DYNAMIC,
            type,
          },
        },
      },
    },
  });
}

export function updateImmutableTextOverlay(
  state: VideoTemplateStateContent,
  id: string,
  textOverlay: DeepImmutableMap<ITextOverlayV2>,
): VideoTemplateStateContent {
  return updateTextOverlay(state, id, overlayToState(textOverlay));
}

export function setTextDimensions(
  state: VideoTemplateStateContent,
  id: string,
  placement: Dimensions<Pixels>,
): VideoTemplateStateContent {
  const { canvas } = state;
  const { height, width, top, left } = measurementToViewport(placement, canvas);

  return updateState(state, {
    textOverlays: {
      data: {
        [id]: {
          size: { height, width },
          position: { top, left },
        },
      },
    },
  });
}

export function deleteTextOverlay(
  state: VideoTemplateStateContent,
  textOverlayId: string,
): VideoTemplateStateContent {
  const { textOverlays } = state;

  const { layerId } = textOverlays.data[textOverlayId];
  const nAssetsInLayer =
    textOverlays.order
      .map(id => textOverlays.data[id].layerId)
      .filter(overlayLayerId => overlayLayerId === layerId).length - 1;

  return {
    ...updateState(state, {
      textOverlays: {
        data: {
          ...textOverlays.data,
          [textOverlayId]: undefined,
        },
        order: textOverlays.order.filter(id => id !== textOverlayId),
      },
    }),
    ...(nAssetsInLayer === 0 ? deleteLayer(state, layerId) : undefined),
  };
}
